
import { Toaster } from "@/components/ui/toaster";
// Import our custom Sonner wrapper instead of directly from sonner
import { Toaster as Sonner } from "@/components/ui/sonner-wrapper";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { AuthPage } from "@/components/auth/AuthPage";
import { Homepage } from "@/components/homepage/Homepage";
import { ClinicAssociationFlow } from "@/components/clinic/ClinicAssociationFlow";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { useState, useEffect } from "@/utils/react-singleton";
// Import our custom ThemeProvider wrapper
import { ThemeProvider } from "@/providers/ThemeProviderWrapper";

const queryClient = new QueryClient();

function AppContent() {
  // Get auth state and setDemoRole function
  const { user, profile, isLoading, logout, setDemoRole } = useAuth();
  // Add state to track current view: 'homepage', 'auth', or 'app'
  // In demo mode, start directly with the app if user exists
  const [currentView, setCurrentView] = useState<'homepage' | 'auth' | 'app'>(
    user ? 'app' : 'homepage'
  );

  // Handle role selection from homepage
  const handleRoleSelect = (role: 'patient' | 'doctor') => {
    setDemoRole(role);
    setCurrentView('app');
  };

  // Effect to handle transitions between authenticated and unauthenticated states
  useEffect(() => {
    if (user && !isLoading) {
      setCurrentView('app');
    } else if (!user && !isLoading && currentView === 'app') {
      // Reset to homepage when user logs out from app
      setCurrentView('homepage');
    }
  }, [user, isLoading, currentView]);

  // Loading indicator
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-64 mx-auto mb-4 animate-pulse">
            <img src="/clinicaflow-logo.svg" alt="ClinicaFlow" className="w-full" />
          </div>
          <p className="text-gray-600">Loading ClinicaFlow...</p>
        </div>
      </div>
    );
  }

  // Show homepage first
  if (currentView === 'homepage') {
    return <Homepage onRoleSelect={handleRoleSelect} />;
  }

  // Show auth page when user wants to sign in
  if (currentView === 'auth') {
    return <AuthPage key={`auth-${Date.now()}`} onAuthSuccess={() => setCurrentView('app')} />;
  }

  // After authentication, if staff/doctor/admin needs clinic association
  if (profile && ['doctor', 'admin', 'staff'].includes(profile.role) && !profile.clinic_id) {
    return (
      <div className="min-h-screen">
        <div className="p-4 bg-blue-600 text-white flex justify-between items-center">
          <div className="h-10">
            <img src="/clinicaflow-logo.svg" alt="ClinicaFlow" className="h-full" />
          </div>
          <button 
            onClick={() => {
              logout();
              setCurrentView('homepage');
            }}
            className="px-3 py-1 bg-blue-700 hover:bg-blue-800 rounded"
          >
            Sign Out
          </button>
        </div>
        <ClinicAssociationFlow />
      </div>
    );
  }
  
  // For patients, redirect to the patient routes
  if (profile && profile.role === 'patient') {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/*" element={<Index />} />
          <Route path="/404" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/*" element={<Index />} />
        <Route path="/404" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    {/* Use our custom ThemeProvider that uses the global React instance */}
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <TooltipProvider>
        <AuthProvider>
          <Toaster />
          <Sonner />
          <AppContent />
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
